import z from "zod";

import { executionSourceSchema } from "./execution-source";
import { importLogEntryPascalScehma } from "./import-log";

export const importLogStatusSchema = z.enum(["Information", "Warning", "Error", "SystemError"]);

export const importLogSourceSchema = z.enum([
	"System",
	"DataMapper",
	"RuleEngine",
	"FinalValidation",
	"FileDropClient",
	"SavedJobResolution",
	"SecureLabelSending",
	"ImportClient",
	"CommandProcessing",
	"Import",
]);

export const importPipelineConditionalMetadataSchema = z.object({
	Field: z.string().optional(),
	Operator: z.string().optional(),
	ValueExpression: z.string().optional(),
});

export const importPipelineFieldValueSchema = z.object({
	FieldFriendlyName: z.string().optional(),
	PackNetField: z.string().optional(),
	CurrentValue: z
		.union([z.string(), z.number(), z.boolean(), z.array(z.unknown()), z.record(z.string(), z.unknown()), z.null()])
		.optional(),
	NewValue: z
		.union([z.string(), z.number(), z.boolean(), z.array(z.unknown()), z.record(z.string(), z.unknown()), z.null()])
		.optional(),
	AllowedValues: z.union([z.array(z.string()), z.string()]).optional(),
	DeliverableType: z.string().optional(),
	DeliverableId: z.string().optional(),
	CustomerHeader: z.string().optional(),
	FieldType: z.string().optional(),
	ErrorCode: z.string().optional(),
	ErrorParams: z.record(z.string(), z.unknown()).optional(),
});

const dimensionCurrentValueSchema = z.object({
	Length: z.union([z.number(), z.string()]).optional(),
	Width: z.union([z.number(), z.string()]).optional(),
	Height: z.union([z.number(), z.string()]).optional(),
});

const metadataValueSchema = z.union([
	z.string(),
	z.number(),
	z.boolean(),
	z.array(z.unknown()),
	z.record(z.string(), z.unknown()),
	z.null(),
]);

export const warningLogMetadataSchema = z
	.object({
		Reason: z.string().optional(),
		Headers: z.array(z.string()).optional(),
		Header: z.string().optional(),
		RuleName: z.string().optional(),
		ActionType: z.string().optional(),
		ExecutionType: z.string().optional(),
		AlwaysRunRule: z.boolean().optional(),
		DeliverableType: z.string().optional(),
		DeliverableId: z.string().optional(),
		FieldType: z.string().optional(),
		CustomerHeader: z.string().optional(),
		FieldFriendlyName: z.string().optional(),
		PackNetField: z.string().optional(),
		UpdatedFieldCount: z.number().optional(),
		Dependency: z.string().optional(),
		StatusCode: z.string().optional(),
		ExceptionType: z.string().optional(),
		Exception: z.string().optional(),
		Expression: z.string().optional(),
		Condition: z.string().optional(),
		ResolvedFieldValue: z.string().optional(),
		RequiredDimensions: z.array(z.string()).optional(),
		MissingDimensions: z.array(z.string()).optional(),
		MissingProductIds: z.array(z.string()).optional(),
		UnpackedItemIds: z.array(z.string()).optional(),
		Inputs: z.record(z.string(), z.unknown()).optional(),
		ServiceDependencyFailures: z.array(z.string()).optional(),
		Quantity: z.union([z.number(), z.string()]).optional(),
		SavedJobAlias: z.string().optional(),
		Conditional: importPipelineConditionalMetadataSchema.optional(),
		CurrentValue: dimensionCurrentValueSchema.optional(),
		NewValue: metadataValueSchema.optional(),
		AllowedValues: z.union([z.array(z.string()), z.string()]).optional(),
		ErrorCode: z.string().optional(),
		ErrorParams: z.record(z.string(), z.unknown()).optional(),
		Fields: z.array(importPipelineFieldValueSchema).optional(),
		Field: importPipelineFieldValueSchema.optional(),
	})
	.catchall(z.unknown());

export const importPipelineLogEntrySchema = importLogEntryPascalScehma.extend({
	Source: importLogSourceSchema,
	Status: importLogStatusSchema,
	Message: z.string(),
	MessageType: z.string(),
	Metadata: warningLogMetadataSchema.optional(),
});

export const importPipelineImportTimingSchema = z.object({
	StepTimingsMs: z.record(z.string(), z.number()).optional(),
	DataMapping: z.string().optional(),
	RuleEngine: z.string().optional(),
	Validation: z.string().optional(),
	Overall: z.string().optional(),
	OverallMs: z.number().optional(),
	Overhead: z.string().optional(),
	RuleEngineStart: z.string().optional(),
	ImportStart: z.string().optional(),
	ImportEnd: z.string().optional(),
});

export const importPipelineCartonDataSchema = z
	.object({
		Id: z.string().optional(),
		MediaStyle: z.string().optional(),
	})
	.catchall(z.unknown());

export const importPipelineAuditDataSchema = z.object({
	ExecutionSource: executionSourceSchema.optional(),
	ImportTiming: importPipelineImportTimingSchema.optional(),
	ImportLog: z.array(importPipelineLogEntrySchema).optional(),
	Cartons: z.array(importPipelineCartonDataSchema).optional(),
});

export const importPipelineWarningDataSchema = z.object({
	ImportLog: z.array(importPipelineLogEntrySchema).optional(),
});

export const ignoredWorkMetadataSchema = z.object({
	SampleInternalUniqueId: z.string().optional(),
	LPN: z.string().optional(),
	BatchId: z.string().optional(),
	SampleStatus: z.string().optional(),
	BatchQuantity: z.number().optional(),
	RuleName: z.string().optional(),
	ActionType: z.string().optional(),
	Reason: z.string().optional(),
});

export const importIgnoredAuditTrailDataSchema = z.object({
	IgnoredWorkMetadata: ignoredWorkMetadataSchema.optional(),
});

export type ImportLogStatus = z.infer<typeof importLogStatusSchema>;
export type ImportLogSource = z.infer<typeof importLogSourceSchema>;
export type ImportPipelineConditionalMetadata = z.infer<typeof importPipelineConditionalMetadataSchema>;
export type ImportPipelineFieldValue = z.infer<typeof importPipelineFieldValueSchema>;
export type WarningLogMetadata = z.infer<typeof warningLogMetadataSchema>;
export type ImportPipelineLogEntry = z.infer<typeof importPipelineLogEntrySchema>;
export type ImportPipelineImportTiming = z.infer<typeof importPipelineImportTimingSchema>;
export type ImportPipelineCartonData = z.infer<typeof importPipelineCartonDataSchema>;
export type ImportPipelineAuditData = z.infer<typeof importPipelineAuditDataSchema>;
export type ImportPipelineWarningData = z.infer<typeof importPipelineWarningDataSchema>;
export type IgnoredWorkMetadata = z.infer<typeof ignoredWorkMetadataSchema>;
export type ImportIgnoredAuditTrailData = z.infer<typeof importIgnoredAuditTrailDataSchema>;
export type WarningImportLogEntry = ImportPipelineLogEntry;
